/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { AvatarContent } from '@atlaskit/avatar';
import AvatarGroup, { type AvatarProps as AvatarGroupItemProps } from '@atlaskit/avatar-group';
import AkFlag, { type FlagProps } from '@atlaskit/flag';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token, useThemeObserver } from '@atlaskit/tokens';

import { type InviteOnThirdPartyLinkUsers, type LinkContext } from '../../common/types';
import { withLocalIntl } from '../../common/utils/i18n/intl-provider';

import mailtoToConfluenceIconDark from './assets/mailto-to-confluence-icon-dark.svg';
import mailtoToConfluenceIcon from './assets/mailto-to-confluence-icon.svg';
import mailtoToJiraIconDark from './assets/mailto-to-jira-icon-dark.svg';
import mailtoToJiraIcon from './assets/mailto-to-jira-icon.svg';
import slackToConfluenceIconDark from './assets/slack-to-confluence-icon-dark.svg';
import slackToConfluenceIcon from './assets/slack-to-confluence-icon.svg';
import slackToJiraIconDark from './assets/slack-to-jira-icon-dark.svg';
import slackToJiraIcon from './assets/slack-to-jira-icon.svg';
import { messages } from './messages';

export type DismissMethod = 'closeButton' | 'notNowButton' | 'dontShowAgainButton';

export type Props = {
	/** Flag id passed through to AkFlag. */
	id: FlagProps['id'];
	/** Whether this flag was triggered by a conversation link or a profile link. */
	linkContext?: LinkContext;
	/**
	 * Observer invoked synchronously when the user clicks the invite CTA. 
	 * Intended for capturing product-side analytics.
	 */
	onActioned?: () => void;
	/** Called when the flag is dismissed. */
	onDismiss?: (id: FlagProps['id'], dismissMethod: DismissMethod) => void;
	/** Called when the user confirms the invite CTA. */
	onInvite?: () => Promise<void>;
	/** Glyph to render inside the destination product tile of the composite icon. */
	productIcon?: React.ReactNode;
	/** Destination product the link points to. */
	productName: Product;
	/** Whether to show a "Don't show again" permanent dismiss button. */
	showDontShowAgain?: boolean;
	/** Channel the link was shared through. */
	source: Source;
	/** Display name of the destination space / site that the link points to. */
	spaceName?: string;
	/** Users who do not yet have access to the linked resource. */
	usersWithoutAccess: InviteOnThirdPartyLinkUsers;
};

type Product = 'Jira' | 'Confluence';
type Source = 'mailto' | 'slack';
type IconSet = { dark: string; light: string };

const MAX_AVATARS = 5;

const styles = {
	avatarBackground: css({
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
		height: '100%',
		backgroundColor: token('color.background.brand.bold'),
	}),
	avatarInitial: css({
		color: token('color.text.inverse'),
		fontWeight: token('font.weight.semibold'),
		textAlign: 'center',
	}),
};

const toAvatarData = (users: InviteOnThirdPartyLinkUsers): AvatarGroupItemProps[] =>
	users.map((user) => {
		const displayName = user.name ?? user.email ?? '';
		return user.avatar
			? { key: user.id, name: displayName, src: user.avatar }
			: {
					key: user.id,
					name: displayName,
					borderColor: token('color.border.inverse'),
					children: (
						<AvatarContent>
							<div css={styles.avatarBackground}>
								<span css={styles.avatarInitial}>{displayName.trim()[0]?.toUpperCase() ?? ''}</span>
							</div>
						</AvatarContent>
					),
				};
	});

const ICON_MATRIX: Record<Source, Record<Product, IconSet>> = {
	mailto: {
		Jira: { light: mailtoToJiraIcon, dark: mailtoToJiraIconDark },
		Confluence: { light: mailtoToConfluenceIcon, dark: mailtoToConfluenceIconDark },
	},
	slack: {
		Jira: { light: slackToJiraIcon, dark: slackToJiraIconDark },
		Confluence: { light: slackToConfluenceIcon, dark: slackToConfluenceIconDark },
	},
} as const;

/**
 * InviteOnThirdPartyLinkFlag
 *
 * Shown when a Slack or mailto link is pasted in Jira or Confluence,
 * and there are users in that context that don't have access to the current
 * product.
 * Renders a composite Slack or mailto + product icon, a stacked avatar group
 * of the affected teammates, a contextual title / description, and an invite
 * / dismiss action pair.
 *
 */
export const InviteOnThirdPartyLinkFlagNoIntl = ({
	usersWithoutAccess,
	onActioned,
	onInvite,
	onDismiss,
	productIcon,
	productName,
	showDontShowAgain,
	spaceName,
	source,
	...props
}: Props): JSX.Element | null => {
	const { formatMessage } = useIntl();

	const { colorMode } = useThemeObserver();
	const isDark = colorMode === 'dark';

	// Early return for empty usersWithoutAccess array.
	if (usersWithoutAccess.length === 0) {
		return null;
	}

	const iconSet = ICON_MATRIX[source][productName];
	const flagIcon = isDark ? iconSet.dark : iconSet.light;

	const userCount = usersWithoutAccess.length;
	const isSingleUser = userCount === 1;
	const firstUser = usersWithoutAccess[0];
	const isNonUser = source === 'mailto' && !firstUser.id;

	const showUserIdentity = isSingleUser && source === 'slack';
	const singleUserName = firstUser.name || firstUser.email;

	const singleUserTitle = showUserIdentity
		? singleUserName
		: formatMessage(messages.fallbackSingleUserTitle);

	const singleUserTitleForDescription = showUserIdentity ? singleUserName : firstUser.email;

	const avatarData = toAvatarData(usersWithoutAccess);

	const actions = [
		{
			content: formatMessage(messages.ctaInvite, { userCount }),
			onClick: () => {
				onActioned?.();
				return onInvite?.();
			},
		},
		...(showDontShowAgain
			? [
					{
						content: formatMessage(messages.ctaDontShowAgain),
						onClick: () => onDismiss?.(props.id, 'dontShowAgainButton'),
					},
				]
			: [		{
			content: formatMessage(messages.ctaNotNow),
			onClick: () => onDismiss?.(props.id, 'notNowButton'),
		},]),
	];

	const titleText = formatMessage(messages.titleInviteUser, {
		userCount,
		...(isSingleUser ? { singleUserTitle } : {}),
		productName,
	});

	const descriptionText = !isNonUser
		? formatMessage(messages.descriptionInviteUser, {
				userCount,
				...(isSingleUser ? { singleUserTitle: singleUserTitleForDescription } : {}),
				source,
				spaceName,
			})
		: formatMessage(messages.descriptionInviteNonUser, {
				email: firstUser?.email,
				productName,
				spaceName,
			});

	return (
		<AkFlag
			{...props}
			testId={`invite-on-third-party-${source}-link-flag`}
			icon={<img src={flagIcon} alt={productName} />}
			onDismissed={(id) => onDismiss?.(id, 'closeButton')}
			title={
				<Stack alignInline="start">
					{!isSingleUser && avatarData.length > 0 && (
						<AvatarGroup
							appearance="stack"
							size="small"
							maxCount={MAX_AVATARS}
							data={avatarData}
							label={formatMessage(messages.avatarGroupLabel)}
							testId={`invite-on-third-party-${source}-link-flag-avatars`}
							shouldPopupRenderToParent
						/>
					)}
					<Box as="p">{titleText}</Box>
				</Stack>
			}
			description={<Text as="p">{descriptionText}</Text>}
			actions={actions}
		/>
	);
};

export const InviteOnThirdPartyLinkFlag: (props: Props) => React.JSX.Element | null = withLocalIntl(
	InviteOnThirdPartyLinkFlagNoIntl,
);
